/**
 * Хук для проверки прав доступа
 */

import { useState, useEffect, useCallback } from 'react';
import { SYSTEM_PERMISSIONS } from '@masters-club/types';
import {
  loadPermissions,
  clearPermissionsCache,
  hasPermission as checkPermission
} from '@/lib/permissions-cache';

// Re-export для обратной совместимости
export { clearPermissionsCache };

/**
 * Хук для проверки наличия конкретного права
 */
export function usePermission(permissionCode: string): {
  hasPermission: boolean;
  isLoading: boolean;
} {
  const [hasPermission, setHasPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const check = async () => {
      const permissions = await loadPermissions();

      if (!mounted) {return;}

      const has = checkPermission(permissions, permissionCode);

      setHasPermission(has);
      setIsLoading(false);
    };

    check();

    return () => {
      mounted = false;
    };
  }, [permissionCode]);

  return { hasPermission, isLoading };
}

/**
 * Хук для проверки права на AI ассистента
 */
export function useCanUseAiAssistant(): {
  canUse: boolean;
  isLoading: boolean;
} {
  const { hasPermission, isLoading } = usePermission(
    SYSTEM_PERMISSIONS.AI_ASSISTANT_USE
  );

  return { canUse: hasPermission, isLoading };
}

/**
 * Хук для получения всех прав пользователя
 */
export function usePermissions(): {
  permissions: string[];
  isLoading: boolean;
  refetch: () => Promise<void>;
} {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    clearPermissionsCache();
    const perms = await loadPermissions();
    setPermissions(perms);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const perms = await loadPermissions();
      if (mounted) {
        setPermissions(perms);
        setIsLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  return { permissions, isLoading, refetch };
}

/**
 * Маппинг ресурсов к кодам прав
 */
const resourcePermissionMap: Record<string, string> = {
  'admin-users': 'admin-users',
  users: 'users',
  roles: 'rbac.roles',
  permissions: 'rbac.permissions',
  subscriptions: 'subscriptions',
  'subscription-plans': 'subscription-plans',
  'channel-passes': 'channel-passes',
  payments: 'payments',
  orders: 'orders',
  products: 'products',
  'knowledge-documents': 'knowledge',
  broadcasts: 'broadcasts',
  'support-tickets': 'support',
  'bot-tokens': 'api-keys',
  integrations: 'integrations',
  // Библиотека голосов — часть перевода эфиров, живёт под правами эфиров.
  // Маппинг обязан совпадать с access-control-provider: это два независимых
  // словаря, и расхождение делает пункт меню серым при живом доступе.
  'voice-profiles': 'livestreams',
  'daily-stats': 'analytics',
  courses: 'courses',
  banners: 'content',
  'mini-banners': 'content',
  articles: 'content',
  announcements: 'content',
  teachers: 'content',
  'telegram-channels': 'telegram-channels',
  'channel-memberships': 'channel-memberships',
  'telegram-client': 'telegram-client',
  triggers: 'triggers',
  languages: 'localization',
  'translation-keys/ui': 'localization',
  'translation-keys/bot': 'localization',
  ai: 'ai',
  messaging: 'support',
  moderation: 'moderation',
  posts: 'moderation',
  reports: 'reports',
  violations: 'violations',
  'admin/currencies': 'currencies',
  'staff-analytics': 'staff-analytics',
  'audit-logs': 'audit-logs',
  'order-statuses': 'order-statuses',
  'user-invitations': 'user-invitations',
  'user-entitlements': 'users',
  'club-settings': 'settings',
  'legal-pages': 'legal-pages',
  'user-bonuses': 'user-bonuses',
  'telegram-bots': 'telegram-bots',
  'pricing-tiers': 'pricing-tiers',
  leads: 'crm.leads',
  'lead-funnels': 'crm.leads',
  'lead-webhooks': 'crm.leads',
  'message-templates': 'email-templates',
  tags: 'tags',
  'utm-sources': 'utm-sources',
  'admin/countries': 'countries',
  'promo-codes': 'promo-codes',
  discounts: 'promo-codes',
};

/**
 * Проверяет, имеет ли пользователь право на ресурс
 */
function hasResourcePermission(
  permissions: string[],
  resource: string
): boolean {
  // Dashboard всегда доступен
  if (resource === 'dashboard') {
    return true;
  }

  // Super admin имеет все права
  if (permissions.includes('*') || permissions.includes('admin.*')) {
    return true;
  }

  // Livestreams: пункт меню при наличии canonical CRUD livestreams.* ИЛИ
  // operational livestream.* (host/manage/analytics).
  if (resource === 'livestreams') {
    return permissions.some(
      (p) => p.startsWith('livestreams.') || p.startsWith('livestream.'),
    );
  }

  // Шаблоны сообщений: бэкенд защищает их единым правом
  // email-templates.manage (нет отдельного .read), пункт меню по нему же.
  if (resource === 'message-templates') {
    return checkPermission(permissions, 'email-templates.manage');
  }

  // Базы данных (тенанты): пункт меню — по праву просмотра admin.databases.view.
  if (resource === 'databases') {
    return checkPermission(permissions, 'admin.databases.view');
  }

  // MCP-доступ: пункт меню по праву mcp.use (у раздела нет .read).
  if (resource === 'mcp') {
    return checkPermission(permissions, SYSTEM_PERMISSIONS.MCP_USE);
  }

  const permissionPrefix = resourcePermissionMap[resource] ?? resource;

  // analytics: канонический «read» — .view (нет analytics.read в каталоге)
  if (permissionPrefix === 'analytics') {
    return checkPermission(permissions, 'analytics.view');
  }

  const readPermission = `${permissionPrefix}.read`;

  if (permissionPrefix === 'crm.leads' && checkPermission(permissions, 'crm.read')) {
    return true;
  }

  return checkPermission(permissions, readPermission);
}

/**
 * Хук для проверки прав доступа к элементам меню
 * Возвращает Map с информацией о доступности каждого ресурса
 */
export function useMenuPermissions(resourceNames: string[]): {
  permissionMap: Map<string, boolean>;
  isLoading: boolean;
} {
  const [permissionMap, setPermissionMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState(true);

  // Memoize dependency to avoid ESLint warning about complex expression
  const resourceKey = resourceNames.join(',');

  useEffect(() => {
    let mounted = true;

    const checkPermissions = async () => {
      const permissions = await loadPermissions();

      if (!mounted) {return;}

      const newMap = new Map<string, boolean>();

      // Используем resourceKey для парсинга ресурсов вместо resourceNames
      // чтобы избежать бесконечного цикла из-за сравнения массивов по ссылке
      const resources = resourceKey ? resourceKey.split(',') : [];
      for (const resource of resources) {
        newMap.set(resource, hasResourcePermission(permissions, resource));
      }

      setPermissionMap(newMap);
      setIsLoading(false);
    };

    checkPermissions();

    return () => {
      mounted = false;
    };
  }, [resourceKey]);

  return { permissionMap, isLoading };
}

// Re-export useServerErrors для удобства
export { useServerErrors, type FieldErrors, type UseServerErrorsResult } from './useServerErrors';
